// Чистые форматтеры — отдельный модуль для лёгких юнит-тестов без DOM.

export function formatCount(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString("en-US");
}

export function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "никогда";
  const then = new Date(iso).getTime();
  const diffMs = now.getTime() - then;
  if (diffMs < 0) return "только что";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}с назад`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}ч назад`;
  const d = Math.floor(h / 24);
  return `${d}д назад`;
}

export function formatPercentDelta(prev: number, curr: number): string {
  if (prev === 0) return curr === 0 ? "0%" : "+∞%";
  const pct = ((curr - prev) / prev) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

export function kindLabel(kind: string): string {
  switch (kind) {
    case "sticker":
      return "Стикер";
    case "keychain":
      return "Брелок";
    case "skin":
      return "Скин";
    default:
      return kind;
  }
}

// Человекочитаемая подпись фильтра скина: «FN · Normal», «FT · StatTrak», «Любое».
// На вход — filter из ItemDto (для скина: {min,max,category?}). Возвращает "" для
// не-скинов, чтобы вызывать без проверки kind на стороне UI.
export function skinFilterLabel(filter: {
  kind: string;
  min?: number;
  max?: number;
  category?: "normal" | "stattrak" | "souvenir" | undefined;
}): string {
  if (filter.kind !== "skin") return "";
  const wear = wearShort(filter.min ?? 0, filter.max ?? 1);
  const cat = filter.category
    ? filter.category === "normal"
      ? "Normal"
      : filter.category === "stattrak"
        ? "StatTrak"
        : "Souvenir"
    : null;
  return cat ? `${wear} · ${cat}` : wear;
}

// Сопоставляем диапазон float'а со стандартными CS2-wear'ами.
// У скинов часто физический диапазон float'а ограничен (M4A1-S | Solitude
// идёт от 0 до 0.7), поэтому effectiveRange обрезает «Любое» до [0, 0.7].
// Чтобы это не превращалось в визуальный мусор «0–0.7», считаем wear'ом любой
// диапазон, который ПОЛНОСТЬЮ вмещается в стандартный bucket и совпадает с
// ним хотя бы по одной границе (другая граница — обрезание физикой скина).
const WEAR_BUCKETS: Array<{ short: string; min: number; max: number }> = [
  { short: "FN", min: 0.0, max: 0.07 },
  { short: "MW", min: 0.07, max: 0.15 },
  { short: "FT", min: 0.15, max: 0.38 },
  { short: "WW", min: 0.38, max: 0.45 },
  { short: "BS", min: 0.45, max: 1.0 },
];

function wearShort(min: number, max: number): string {
  const E = 0.0001;
  const eq = (a: number, b: number) => Math.abs(a - b) < E;

  // 1. Точное совпадение «Любое».
  if (eq(min, 0) && eq(max, 1)) return "Любое";

  // 2. Стандартный wear-bucket с возможным усечением одной границы по физике скина.
  for (const w of WEAR_BUCKETS) {
    const contained = min >= w.min - E && max <= w.max + E;
    if (contained && (eq(min, w.min) || eq(max, w.max))) return w.short;
  }

  // 3. «Любое» с обрезанием максимума (например [0, 0.7] для скина с physMax=0.7).
  if (eq(min, 0) && max < 1 - E) return "Любое";

  // 4. Действительно произвольный диапазон.
  return `${min}–${max}`;
}
